import { Play } from "lucide-react";
import { cn } from "@/lib/utils";
import { hofPreviewUrl, isHofUploadedVideo, type HofMediaType } from "@/lib/hall-of-fame.helpers";

export type HallOfFameTileEntry = {
  id: string;
  title: string;
  subtitle: string | null;
  entry_year: number;
  media_type: HofMediaType | string;
  media_url: string;
};

type HallOfFameTileProps = {
  entry: HallOfFameTileEntry;
  selected: boolean;
  onSelect: () => void;
};

export function HallOfFameTile({ entry, selected, onSelect }: HallOfFameTileProps) {
  const mediaType: HofMediaType = entry.media_type === "video" ? "video" : "image";
  const preview = hofPreviewUrl(mediaType, entry.media_url);
  const showVideoElement = isHofUploadedVideo(mediaType, entry.media_url);

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "group relative w-[11.5rem] shrink-0 snap-start text-left transition md:w-56",
        selected ? "opacity-100" : "opacity-80 hover:opacity-100",
      )}
      aria-pressed={selected}
    >
      <div
        className={cn(
          "relative aspect-[3/4] overflow-hidden border bg-ink-soft",
          selected ? "border-ember" : "border-white/10",
        )}
      >
        {showVideoElement ? (
          <video
            src={entry.media_url}
            className="h-full w-full object-cover"
            muted
            playsInline
            preload="metadata"
            aria-hidden
          />
        ) : (
          <img
            src={preview}
            alt=""
            className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
            loading="lazy"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent" />
        {mediaType === "video" ? (
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/40 bg-black/40 text-white backdrop-blur-sm">
              <Play className="h-5 w-5 fill-current" />
            </span>
          </span>
        ) : null}
        <span className="absolute left-3 top-3 text-[11px] uppercase tracking-[0.18em] text-white/85">
          {entry.entry_year}
        </span>
        <div className="absolute inset-x-0 bottom-0 p-3">
          <div className="font-display text-xl leading-none tracking-wide text-white md:text-2xl">
            {entry.title}
          </div>
          {entry.subtitle ? (
            <div className="mt-1 text-[10px] uppercase tracking-[0.16em] text-white/70">
              {entry.subtitle}
            </div>
          ) : null}
        </div>
      </div>
      <div
        className={cn(
          "mx-auto mt-2 h-0.5 w-10 transition-colors",
          selected ? "bg-ember-fill" : "bg-transparent",
        )}
      />
    </button>
  );
}
